import React, { useContext, useEffect } from 'react';
import {
  Navigate,
  useLocation,
  useNavigate,
  useParams
} from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Alert, Box, Snackbar } from '@mui/material';
import styles from './styles';
import BotBuilderSidebar from './BotBuilderSidebar/BotBuilderSidebar';
import UserContext from '../../context/UserContext/UserContext';
import ErrorMessage from '../../components/ErrorMessage/ErrorMessage';
import LoadingSpinner from '../../components/LoadingSpinner/LoadingSpinner';
import BotBuilderHeader from './BotBuilderHeader/BotBuilderHeader';
import BotBuilderService from '../../services/BotBuilderService/BotBuilderService';
import Intent from '../../types/BotBuilder/Intent';
import {
  apiToDomain,
  createEmptyIntent,
  domainToApi
} from './utils/intentutils';
import BotResponseTreeNode from '../../types/BotBuilder/BotResponseTreeNode';
import BotBuilder from './BotBuilder/BotBuilder';
import EmbedContext from '../../context/EmbedContext/EmbedContext';
import useChatbotEmbed from '../../hooks/useChatbotEmbed';
import ChatbotContext from '../../context/ChatbotContext/ChatbotContext';
import useAuthRoles from '../../hooks/useAuthRoles';
import BotBuilderContext from '../../context/BotBuilderContext/BotBuilderContext';
import IntentFormData from '../../types/BotBuilder/IntentFormData';
import FloatingTestBot from './FloatingTestBot/FloatingTestBot';
import CustomModal from '../../components/CustomModal/CustomModal';
import Button from '../../components/Button/Button';

function BotBuilderPage(): React.ReactElement {
  const {
    intentHasChanged,
    setIntentHasChanged,
    intent,
    onNodeUpdate,
    onNodeDelete,
    setIntent,
    startInitializing,
    finishInitializing
  } = useContext(BotBuilderContext);

  const { chatbotId, intentId } = useParams();
  const { user } = useContext(UserContext);
  const { currentChatbot } = React.useContext(ChatbotContext);
  const { unconnectedNodes, setUnconnectedNodes } =
    React.useContext(BotBuilderContext);
  const [selectedNode, setSelectedNode] =
    React.useState<BotResponseTreeNode | null>(null);
  const [selectionTick, setSelectionTick] = React.useState<number>(0);
  const [isLoading, setIsLoading] = React.useState<boolean>(true);
  const [errorMessage, setErrorMessage] = React.useState<string>('');
  const [saveIntentSuccessMessage, setSaveIntentSuccessMessage] =
    React.useState<string>('');
  const [saveIntentErrorMessages, setSaveIntentErrorMessages] = React.useState<
    string[]
  >([]);
  const [showSuccessSnackbar, setShowSuccessSnackbar] =
    React.useState<boolean>(false);
  const [showErrorSnackbar, setShowErrorSnackbar] =
    React.useState<boolean>(false);
  const [newIntentIsLoading, setNewIntentIsLoading] =
    React.useState<boolean>(false);
  const [formDataHasChanged, setFormDataHasChanged] =
    React.useState<boolean>(false);
  const [formData, setFormData] = React.useState<IntentFormData>({
    name: '',
    description: null,
    category: null,
    safeguarding: false,
    safeguardingRecipient: null,
    locked: false
  });

  const { embedIsReady, setEmbedIsReady } = useContext(EmbedContext);
  const [testBotVisible, setTestBotVisible] = React.useState(false);

  const [showConfirmModal, setShowConfirmModal] =
    React.useState<boolean>(false);
  const [isSavingFromConfirm, setIsSavingFromConfirm] =
    React.useState<boolean>(false);
  const [pendingIntentId, setPendingIntentId] = React.useState<number | null>(
    null
  );
  const [confirmAction, setConfirmAction] = React.useState<
    'exit' | 'navigate' | 'new'
  >('exit');

  const isNewIntent = intentId === 'new';

  const onSuccessSnackbarClose = (): void => setShowSuccessSnackbar(false);
  const onErrorSnackbarClose = (): void => setShowErrorSnackbar(false);
  const handleMessageBlockEditIndexChange = (): void => {};

  const navigate = useNavigate();
  const location = useLocation();
  const { userCanWrite, userIsJapetoAdmin } = useAuthRoles();

  const isLocked = intent?.locked === true && !userIsJapetoAdmin();

  const openErrorSnackbar = (messages: string | string[]): void => {
    setSaveIntentErrorMessages(Array.isArray(messages) ? messages : [messages]);
    setSaveIntentSuccessMessage('');
    setShowSuccessSnackbar(false);
    setShowErrorSnackbar(true);
  };

  const openSuccessSnackbar = (message: string): void => {
    setSaveIntentSuccessMessage(message);
    setSaveIntentErrorMessages([]);
    setShowErrorSnackbar(false);
    setShowSuccessSnackbar(true);
  };

  useChatbotEmbed(
    currentChatbot?.uuid as string,
    true,
    setEmbedIsReady,
    undefined,
    true
  );

  const onSaveIntent = async (
    formDataParam?: IntentFormData
  ): Promise<void> => {
    if (!user || !intent || !chatbotId) {
      openErrorSnackbar('Something went wrong. Please try again later.');
      return;
    }

    const handleError = (err: any): void => {
      if (err.apiError?.message) {
        openErrorSnackbar(err.apiError.message);
      } else if (err.message) {
        try {
          const errorMessages = JSON.parse(err.message);
          if (Array.isArray(errorMessages)) {
            openErrorSnackbar(errorMessages);
          } else {
            openErrorSnackbar(err.message);
          }
        } catch {
          openErrorSnackbar(err.message);
        }
      } else {
        openErrorSnackbar('Something went wrong. Please try again later.');
      }
    };

    try {
      const intentWithStatus = {
        ...intent,
        ...(formDataParam || formData)
      };

      if (!intentWithStatus.name || intentWithStatus.name.trim() === '') {
        openErrorSnackbar('Question name is required');
        return;
      }

      const request = domainToApi(intentWithStatus, unconnectedNodes);

      let data;
      if (isNewIntent) {
        data = await BotBuilderService.createNewIntent(
          parseInt(chatbotId, 10),
          request
        );
        const { intent: parsedIntent, unconnectedNodes: unconnected } =
          apiToDomain(data);

        startInitializing(parsedIntent.id!);
        setIntent(parsedIntent);
        setUnconnectedNodes(unconnected);
        setIntentHasChanged(false);
        setFormDataHasChanged(false);
        setTimeout(() => {
          finishInitializing();
        }, 50);

        openSuccessSnackbar('Question created successfully');
        navigate(`/bot-builder/${chatbotId}/${data.id}`);
      } else {
        data = await BotBuilderService.updateIntent(
          parseInt(chatbotId, 10),
          request
        );
        const { intent: parsedIntent, unconnectedNodes: unconnected } =
          apiToDomain(data);

        startInitializing(intent.id!);
        setIntent(parsedIntent);
        setUnconnectedNodes(unconnected);
        setIntentHasChanged(false);
        setFormDataHasChanged(false);
        setTimeout(() => {
          finishInitializing();
        }, 200);

        openSuccessSnackbar('Question updated successfully');
      }
    } catch (err: any) {
      handleError(err);
    }
  };

  const onSelectedNodeChange = (node: BotResponseTreeNode | null): void => {
    setSelectedNode(node);
    setSelectionTick((tick) => tick + 1);
  };

  const onUpdateIntent = (updatedIntent: Intent): void => {
    setIntent(updatedIntent);
    setIntentHasChanged(true);
  };

  useEffect(() => {
    if (isNewIntent) {
      const newIntent = createEmptyIntent();
      setIntent(newIntent);
      setIntentHasChanged(false);
      setSelectedNode(null);

      setFormData({
        name: '',
        description: null,
        category: null,
        safeguarding: false,
        safeguardingRecipient: null,
        locked: false
      });

      setIsLoading(false);
      setNewIntentIsLoading(false);
    }
  }, [user, isNewIntent]);

  useEffect(() => {
    setErrorMessage('');
    setSelectedNode(null);
    if (chatbotId && intentId && !isNewIntent) {
      const intentIdNum = parseInt(intentId, 10);
      startInitializing(intentIdNum);

      BotBuilderService.getIntent(parseInt(chatbotId, 10), intentIdNum)
        .then((data) => {
          const { intent: newIntent, unconnectedNodes: unconnected } =
            apiToDomain(data);
          setIntent(newIntent);
          setUnconnectedNodes(unconnected);
          setFormData({
            name: newIntent.name || '',
            description: newIntent.description || null,
            category: newIntent.category || null,
            safeguarding: newIntent.safeguarding || false,
            safeguardingRecipient: newIntent.safeguardingRecipient || null,
            locked: newIntent.locked || false
          });
          setFormDataHasChanged(false);
          setIntentHasChanged(false);

          setTimeout(() => {
            finishInitializing();
          }, 50);
        })
        .catch((err) => {
          const error =
            err.apiError?.message ||
            'Something went wrong. Please try again later.';
          setErrorMessage(error);
          finishInitializing();
        })
        .finally(() => {
          setIsLoading(false);
          setNewIntentIsLoading(false);
        });
    }
  }, [chatbotId, intentId, isNewIntent, startInitializing, finishInitializing]);

  const hasUnsavedChanges =
    !isLocked &&
    (intentHasChanged ||
      formDataHasChanged ||
      (isNewIntent &&
        (!!formData.name || !!formData.description || !!formData.category)));

  const performExit = (): void => {
    const returnState = location.state as
      | {
          returnQuery?: string;
          returnPage?: number;
          returnResultsPerPage?: number;
        }
      | null
      | undefined;
    if (
      returnState?.returnQuery !== undefined ||
      returnState?.returnPage !== undefined ||
      returnState?.returnResultsPerPage !== undefined
    ) {
      navigate('/training', {
        state: {
          returnQuery: returnState?.returnQuery,
          returnPage: returnState?.returnPage,
          returnResultsPerPage: returnState?.returnResultsPerPage
        }
      });
    } else {
      navigate('/training');
    }
  };

  const performNavigateToIntent = (id: number): void => {
    setNewIntentIsLoading(true);
    navigate(`/bot-builder/${chatbotId}/${id}`);
  };

  const performNavigateToNewIntent = (): void => {
    setNewIntentIsLoading(true);
    navigate(`/bot-builder/${chatbotId}/new`);
  };

  const handleExit = (): void => {
    if (hasUnsavedChanges) {
      setConfirmAction('exit');
      setShowConfirmModal(true);
    } else {
      performExit();
    }
  };

  const handleNavigateToIntent = (id: number): void => {
    if (hasUnsavedChanges) {
      setPendingIntentId(id);
      setConfirmAction('navigate');
      setShowConfirmModal(true);
    } else {
      performNavigateToIntent(id);
    }
  };

  const handleAddNewQuestion = (): void => {
    if (hasUnsavedChanges) {
      setConfirmAction('new');
      setShowConfirmModal(true);
    } else {
      performNavigateToNewIntent();
    }
  };

  const handleConfirmCancel = (): void => {
    setShowConfirmModal(false);
    setPendingIntentId(null);
    if (confirmAction === 'navigate' || confirmAction === 'new') {
      setNewIntentIsLoading(false);
    }
  };

  const proceedAfterConfirm = (): void => {
    if (confirmAction === 'exit') {
      performExit();
    } else if (confirmAction === 'navigate' && pendingIntentId) {
      performNavigateToIntent(pendingIntentId);
    } else if (confirmAction === 'new') {
      performNavigateToNewIntent();
    }
  };

  const handleSaveAndProceed = async (): Promise<void> => {
    setIsSavingFromConfirm(true);
    try {
      await onSaveIntent(formData);
      setShowConfirmModal(false);
      proceedAfterConfirm();
    } finally {
      setIsSavingFromConfirm(false);
      setPendingIntentId(null);
    }
  };

  const handleDiscardAndProceed = (): void => {
    setShowConfirmModal(false);
    proceedAfterConfirm();
    setPendingIntentId(null);
  };

  if (!userCanWrite()) {
    return <Navigate to='/chats' state={{ from: location }} replace />;
  }

  return (
    <Box sx={styles.pageContainer}>
      <Helmet>
        <title>Japeto - Bot Builder</title>
      </Helmet>

      {intent && (
        <BotBuilderHeader
          intent={intent}
          embedIsReady={embedIsReady}
          testBotVisible={testBotVisible}
          onToggleTestBot={() => setTestBotVisible((v) => !v)}
          onExit={handleExit}
          onNavigateToIntent={handleNavigateToIntent}
          onAddNewQuestion={handleAddNewQuestion}
          isNewIntent={isNewIntent}
        />
      )}
      {intent && (
        <CustomModal
          open={showConfirmModal}
          onClose={handleConfirmCancel}
          title='Unsaved changes'
          description={`Do you want to save the changes you made in ${
            intent.name || 'this question'
          }?`}
        >
          <div className='flex gap-3'>
            <Button
              variant='destructive'
              size='full'
              onClick={handleDiscardAndProceed}
              disabled={isSavingFromConfirm}
            >
              Don&apos;t save
            </Button>
            <Button
              variant='outline'
              size='full'
              onClick={handleConfirmCancel}
              disabled={isSavingFromConfirm}
            >
              Cancel
            </Button>
            <Button
              loading={isSavingFromConfirm}
              size='full'
              onClick={handleSaveAndProceed}
            >
              Save
            </Button>
          </div>
        </CustomModal>
      )}
      <Box
        sx={{
          display: 'flex'
        }}
      >
        {errorMessage && (
          <Box sx={styles.loadingContainer}>
            <ErrorMessage message={errorMessage} />
          </Box>
        )}
        {!isLoading && (
          <>
            <BotBuilderSidebar
              intent={intent}
              onUpdateIntent={onUpdateIntent}
              isNewIntent={isNewIntent}
              intentHasChanged={intentHasChanged}
              onSaveIntent={onSaveIntent}
              selectedNode={selectedNode}
              selectionTick={selectionTick}
              onSelectedNodeUpdate={onNodeUpdate}
              onFormDataChanged={setFormDataHasChanged}
              formData={formData}
              setFormData={setFormData}
              isLocked={isLocked}
            />
            <BotBuilder
              intent={intent}
              onSelectedNodeChange={onSelectedNodeChange}
              onMessageBlockEditIndexChange={handleMessageBlockEditIndexChange}
              onNodeUpdate={onNodeUpdate}
              openErrorSnackbar={openErrorSnackbar}
              onNodeDelete={onNodeDelete}
              newIntentIsLoading={newIntentIsLoading}
              setIntentHasChanged={setIntentHasChanged}
              isLocked={isLocked}
            />
          </>
        )}
        {!intent && !errorMessage && (
          <Box sx={styles.loadingContainer}>
            <LoadingSpinner size={200} />
          </Box>
        )}
      </Box>
      <Snackbar
        open={showSuccessSnackbar}
        autoHideDuration={2000}
        onClose={onSuccessSnackbarClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right'
        }}
      >
        <Alert
          onClose={onSuccessSnackbarClose}
          severity='success'
          variant='filled'
          sx={styles.snackbarAlert}
        >
          {saveIntentSuccessMessage}
        </Alert>
      </Snackbar>
      {saveIntentErrorMessages.map((message, index) => (
        <Snackbar
          key={index}
          open={showErrorSnackbar}
          autoHideDuration={5000}
          onClose={onErrorSnackbarClose}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'left'
          }}
          style={{ bottom: `${index * 64}px`, marginBottom: 24 }}
        >
          <Alert
            onClose={onErrorSnackbarClose}
            severity='error'
            variant='filled'
            sx={styles.snackbarAlert}
          >
            {message}
          </Alert>
        </Snackbar>
      ))}
      <FloatingTestBot embedIsReady={embedIsReady} visible={testBotVisible} />
    </Box>
  );
}

export default BotBuilderPage;
